/**
 * Principal Observer Performance - Sanjaya Brand Kit
 * Navy (#254B70), Orange (#F0A820), Teal (#258010)
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { 
  Users, TrendingUp, Star, AlertTriangle,
  CheckCircle, Activity, ChevronRight, RefreshCw,
  Award, BarChart3,
  Home, FileText, Eye, ClipboardList, UserCheck, Phone, Shield, Brain
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import ResponsiveLayout from '../components/ResponsiveLayout';

import { principalNavItems } from '../config/principalNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PrincipalObserverPerformance = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [performanceData, setPerformanceData] = useState(null);
  const [selectedObserver, setSelectedObserver] = useState(null);
  const [observerDetails, setObserverDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [principalName, setPrincipalName] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('principal_token');
    if (!token) {
      navigate('/principal/login');
      return;
    }
    fetchPrincipalName();
    fetchPerformance();
  }, [navigate]);

  const fetchPrincipalName = async () => {
    try {
      const token = localStorage.getItem('principal_token');
      const res = await axios.get(`${BACKEND_URL}/api/principal/dashboard?token=${token}`);
      setPrincipalName(res.data?.principal?.name || 'Principal');
    } catch (error) {
      console.error('Error fetching principal name:', error);
    }
  };

  const fetchPerformance = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('principal_token');
      const response = await axios.get(
        `${BACKEND_URL}/api/principal/observer-performance?token=${token}`
      );
      setPerformanceData(response.data);
    } catch (error) {
      console.error('Error fetching performance:', error);
      toast({ title: "Error", description: "Failed to load performance data", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const fetchObserverDetails = async (observerId) => {
    setLoadingDetails(true);
    try {
      const token = localStorage.getItem('principal_token');
      const response = await axios.get(
        `${BACKEND_URL}/api/principal/observer/${observerId}/details?token=${token}`
      );
      setObserverDetails(response.data);
    } catch (error) {
      console.error('Error fetching details:', error);
      toast({ title: "Error", description: "Failed to load observer details", variant: "destructive" });
    } finally {
      setLoadingDetails(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'excellent': return 'bg-sanjaya-teal/10 text-sanjaya-teal border-sanjaya-teal/30';
      case 'good': return 'bg-sanjaya-navy/10 text-sanjaya-navy border-sanjaya-navy/30';
      case 'needs_attention': return 'bg-sanjaya-orange/10 text-sanjaya-orange border-sanjaya-orange/30';
      default: return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'excellent': return <Award className="w-5 h-5 text-sanjaya-teal" />;
      case 'good': return <CheckCircle className="w-5 h-5 text-sanjaya-navy" />;
      case 'needs_attention': return <AlertTriangle className="w-5 h-5 text-sanjaya-orange" />;
      default: return <Activity className="w-5 h-5 text-slate-600" />;
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('principal_token');
    navigate('/principal/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-12 h-12 sm:w-16 sm:h-16 border-4 border-sanjaya-navy/20 border-t-sanjaya-navy rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-base sm:text-lg text-slate-600 font-medium">Loading performance data...</p>
        </div>
      </div>
    );
  }

  return (
    <ResponsiveLayout
      navItems={principalNavItems}
      userRole="principal"
      userName={principalName}
      onLogout={handleLogout}
    >
      <div data-testid="observer-performance-page" className="font-body">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5 sm:mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-heading font-bold text-sanjaya-navy flex items-center gap-2 sm:gap-3">
              <UserCheck className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-sanjaya-navy" />
              Observer Performance
            </h1>
            <p className="text-sm sm:text-base text-slate-600 mt-1">Monitor engagement metrics</p>
          </div>
          <Button 
            variant="outline" 
            onClick={fetchPerformance}
            disabled={loading}
            className="self-start sm:self-auto h-11 sm:h-12 px-4 sm:px-6 text-sm sm:text-base font-semibold border-sanjaya-navy/30 text-sanjaya-navy hover:bg-sanjaya-navy/5"
          >
            <RefreshCw className={`w-4 h-4 sm:w-5 sm:h-5 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-5 sm:mb-6">
          <div className="bg-sanjaya-navy/10 border-2 border-sanjaya-navy/30 rounded-xl p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <Users className="w-6 h-6 sm:w-8 sm:h-8 text-sanjaya-navy" />
              <div>
                <p className="text-xl sm:text-2xl font-bold text-sanjaya-navy font-heading">{performanceData?.total_observers || 0}</p>
                <p className="text-xs sm:text-sm text-sanjaya-navy/70 font-medium">Total Observers</p>
              </div>
            </div>
          </div>
          
          <div className="bg-sanjaya-teal/10 border-2 border-sanjaya-teal/30 rounded-xl p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <Award className="w-6 h-6 sm:w-8 sm:h-8 text-sanjaya-teal" />
              <div>
                <p className="text-xl sm:text-2xl font-bold text-sanjaya-teal font-heading">{performanceData?.summary?.excellent || 0}</p>
                <p className="text-xs sm:text-sm text-sanjaya-teal/70 font-medium">Excellent</p>
              </div>
            </div>
          </div>
          
          <div className="bg-sanjaya-navy/10 border-2 border-sanjaya-navy/30 rounded-xl p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <CheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-sanjaya-navy" />
              <div>
                <p className="text-xl sm:text-2xl font-bold text-sanjaya-navy font-heading">{performanceData?.summary?.good || 0}</p>
                <p className="text-xs sm:text-sm text-sanjaya-navy/70 font-medium">Good</p>
              </div>
            </div>
          </div>
          
          <div className="bg-sanjaya-orange/10 border-2 border-sanjaya-orange/30 rounded-xl p-3 sm:p-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <AlertTriangle className="w-6 h-6 sm:w-8 sm:h-8 text-sanjaya-orange" />
              <div>
                <p className="text-xl sm:text-2xl font-bold text-sanjaya-orange font-heading">{performanceData?.summary?.needs_attention || 0}</p>
                <p className="text-xs sm:text-sm text-sanjaya-orange/70 font-medium">Needs Attention</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Performance List */}
          <div className="lg:col-span-2">
            <Card className="border-2 border-slate-200 shadow-sm">
              <CardHeader className="pb-3 sm:pb-4">
                <CardTitle className="text-lg sm:text-xl font-heading text-sanjaya-navy">Performance Overview</CardTitle>
                <CardDescription>Click on an observer to view details</CardDescription>
              </CardHeader>
              <CardContent className="max-h-[500px] sm:max-h-[600px] overflow-y-auto">
                {performanceData?.observer_performance?.length === 0 ? (
                  <div className="text-center py-8 sm:py-12 text-slate-500">
                    <Users className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 text-slate-300" />
                    <p className="text-base sm:text-lg font-medium">No observers found</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {performanceData?.observer_performance?.map((item, index) => (
                      <button 
                        key={item.observer.id}
                        className={`w-full p-3 sm:p-4 rounded-xl border-2 transition-all text-left hover:shadow-md ${
                          selectedObserver?.id === item.observer.id 
                            ? 'border-sanjaya-orange bg-sanjaya-orange/5' 
                            : 'border-slate-200 bg-white hover:border-sanjaya-navy/30'
                        }`}
                        onClick={() => {
                          setSelectedObserver(item.observer);
                          fetchObserverDetails(item.observer.id);
                        }}
                        data-testid={`observer-card-${item.observer.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3 sm:gap-4">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-sanjaya-navy rounded-full flex items-center justify-center text-white font-bold text-base sm:text-lg">
                              {index + 1}
                            </div>
                            <div>
                              <p className="font-bold text-slate-800 text-sm sm:text-base font-heading">{item.observer.name}</p>
                              <p className="text-xs sm:text-sm text-slate-600">{item.observer.specialization || 'Observer'}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 sm:gap-4">
                            <span className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-semibold border-2 ${getStatusColor(item.status)}`}>
                              <span className="flex items-center gap-1">
                                {getStatusIcon(item.status)}
                                <span className="hidden sm:inline capitalize">{item.status?.replace('_', ' ')}</span>
                              </span>
                            </span>
                            <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
                          </div>
                        </div>
                        
                        {/* Quick Metrics */}
                        <div className="grid grid-cols-4 gap-2 sm:gap-4 mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-slate-100">
                          <div className="text-center">
                            <p className="text-base sm:text-lg font-bold text-slate-800">{item.metrics.assigned_students}</p>
                            <p className="text-xs text-slate-500">Students</p>
                          </div>
                          <div className="text-center">
                            <p className="text-base sm:text-lg font-bold text-slate-800">{item.metrics.sessions_last_30_days}</p>
                            <p className="text-xs text-slate-500">Sessions</p>
                          </div>
                          <div className="text-center">
                            <p className="text-base sm:text-lg font-bold text-slate-800">{item.metrics.consistency_score}%</p>
                            <p className="text-xs text-slate-500">Consistency</p>
                          </div>
                          <div className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Star className="w-3 h-3 sm:w-4 sm:h-4 text-sanjaya-orange" />
                              <p className="text-base sm:text-lg font-bold text-slate-800">{item.metrics.average_rating || 'N/A'}</p>
                            </div>
                            <p className="text-xs text-slate-500">Rating</p>
                          </div>
                        </div>
                        
                        {/* Consistency Bar */}
                        <div className="mt-3">
                          <div className="flex justify-between text-xs text-slate-500 mb-1">
                            <span>Consistency Score</span>
                            <span>{item.metrics.consistency_score}%</span>
                          </div>
                          <div className="bg-slate-200 rounded-full h-2">
                            <div 
                              className={`h-2 rounded-full transition-all ${
                                item.metrics.consistency_score >= 80 ? 'bg-sanjaya-teal' :
                                item.metrics.consistency_score >= 60 ? 'bg-sanjaya-navy' :
                                'bg-sanjaya-orange'
                              }`}
                              style={{ width: `${item.metrics.consistency_score}%` }}
                            />
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Observer Details Panel */}
          <div className="lg:col-span-1">
            <Card className="border-2 border-slate-200 shadow-sm sticky top-4">
              <CardHeader className="pb-3 sm:pb-4">
                <CardTitle className="text-lg sm:text-xl font-heading text-sanjaya-navy">Observer Details</CardTitle>
                <CardDescription>
                  {selectedObserver ? selectedObserver.name : 'Select an observer'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingDetails ? (
                  <div className="flex items-center justify-center py-8 sm:py-12">
                    <div className="w-8 h-8 border-4 border-sanjaya-navy/20 border-t-sanjaya-navy rounded-full animate-spin"></div>
                  </div>
                ) : observerDetails ? (
                  <div className="space-y-4 sm:space-y-6">
                    {/* Observer Info */}
                    <div className="p-3 sm:p-4 bg-sanjaya-navy/5 rounded-xl border border-sanjaya-navy/10">
                      <p className="font-bold text-base sm:text-lg text-sanjaya-navy font-heading">{observerDetails.observer?.name}</p>
                      <p className="text-sm text-slate-600">{observerDetails.observer?.email}</p>
                      <p className="text-xs sm:text-sm text-slate-500 mt-1">
                        {observerDetails.observer?.specialization || 'General Observer'}
                      </p>
                    </div>

                    {/* Statistics */}
                    <div>
                      <h4 className="font-bold text-slate-800 mb-2 sm:mb-3 text-sm sm:text-base font-heading">Statistics</h4>
                      <div className="grid grid-cols-2 gap-2 sm:gap-3">
                        <div className="p-2 sm:p-3 bg-sanjaya-navy/10 rounded-xl text-center">
                          <p className="text-lg sm:text-xl font-bold text-sanjaya-navy">
                            {observerDetails.statistics?.total_students || 0}
                          </p>
                          <p className="text-xs text-sanjaya-navy/70">Total Students</p>
                        </div>
                        <div className="p-2 sm:p-3 bg-sanjaya-teal/10 rounded-xl text-center">
                          <p className="text-lg sm:text-xl font-bold text-sanjaya-teal">
                            {observerDetails.statistics?.active_students || 0}
                          </p>
                          <p className="text-xs text-sanjaya-teal/70">Active</p>
                        </div>
                      </div>
                    </div>

                    {/* Assigned Students */}
                    <div>
                      <h4 className="font-bold text-slate-800 mb-2 sm:mb-3 text-sm sm:text-base font-heading">Assigned Students</h4>
                      <div className="space-y-2 max-h-32 sm:max-h-40 overflow-y-auto">
                        {observerDetails.assigned_students?.length === 0 ? (
                          <p className="text-sm text-slate-500">No students assigned</p>
                        ) : (
                          observerDetails.assigned_students?.map(student => (
                            <div key={student.id} className="p-2 bg-slate-50 rounded-lg flex justify-between items-center">
                              <span className="text-xs sm:text-sm font-medium text-slate-700">{student.name}</span>
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                student.status === 'active' ? 'bg-sanjaya-teal/10 text-sanjaya-teal' : 'bg-slate-100 text-slate-600'
                              }`}>
                                {student.status || 'active'}
                              </span>
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Recent Sessions */}
                    <div>
                      <h4 className="font-bold text-slate-800 mb-2 sm:mb-3 text-sm sm:text-base font-heading">Recent Sessions</h4>
                      <div className="space-y-2 max-h-32 sm:max-h-40 overflow-y-auto">
                        {observerDetails.recent_sessions?.length === 0 ? (
                          <p className="text-sm text-slate-500">No sessions recorded</p>
                        ) : (
                          observerDetails.recent_sessions?.slice(0, 5).map((session, i) => (
                            <div key={i} className="p-2 bg-slate-50 rounded-lg">
                              <div className="flex justify-between items-center">
                                <span className="text-xs sm:text-sm text-slate-700">{session.child_name || 'Student'}</span>
                                <span className="text-xs text-slate-500">
                                  {new Date(session.created_at).toLocaleDateString()}
                                </span>
                              </div>
                              {session.mood && (
                                <span className="text-xs text-slate-500">Mood: {session.mood}</span>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="pt-3 sm:pt-4 border-t border-slate-200">
                      <Button 
                        className="w-full h-10 sm:h-11 bg-sanjaya-orange hover:bg-sanjaya-orange-dark text-sanjaya-navy font-bold"
                        onClick={() => navigate(`/principal/observer/${selectedObserver.id}`)}
                      >
                        <BarChart3 className="w-4 h-4 mr-2" /> View Full Report
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 sm:py-12 text-slate-500">
                    <Users className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 text-slate-300" />
                    <p className="text-sm sm:text-base font-medium">Select an observer</p>
                    <p className="text-xs sm:text-sm text-slate-400 mt-1">View performance details</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </ResponsiveLayout>
  );
};

export default PrincipalObserverPerformance;
